import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ExternalLink, Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabaseUrl, publicAnonKey } from '@/utils/supabase/info';

interface PublicDocument {
  id: string;
  type: string;
  title: string;
  content: unknown;
  raw_markdown: string | null;
  external_url: string | null;
}

type Block = { heading?: string; title?: string; body?: string; text?: string; items?: string[] };

/**
 * Public, no-login branded reader for a shared document (/p/:token).
 * Calls the no-JWT /public/share/:token edge endpoint (anon key satisfies the platform
 * gate; the endpoint validates the single share token itself).
 */
export function PublicDocumentReader() {
  const { token } = useParams<{ token: string }>();
  const [doc, setDoc] = useState<PublicDocument | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'passcode' | 'gone' | 'error'>('loading');
  const [passcode, setPasscode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [passcodeError, setPasscodeError] = useState<string | null>(null);

  const fetchDoc = useCallback(
    async (pass?: string) => {
      if (!token) {
        setStatus('error');
        return;
      }
      const url = new URL(`${supabaseUrl}/functions/v1/api/public/share/${token}`);
      if (pass) url.searchParams.set('passcode', pass);
      try {
        const res = await fetch(url.toString(), {
          headers: {
            apikey: publicAnonKey,
            Authorization: `Bearer ${publicAnonKey}`,
          },
        });

        if (res.status === 401) {
          const body = await res.json().catch(() => ({}));
          setStatus('passcode');
          if (pass) setPasscodeError(body.error === 'invalid_passcode' ? 'Incorrect passcode.' : 'Passcode required.');
          return;
        }
        if (res.status === 404 || res.status === 410) {
          setStatus('gone');
          return;
        }
        if (!res.ok) {
          setStatus('error');
          return;
        }
        setDoc(await res.json());
        setStatus('ready');
      } catch {
        setStatus('error');
      }
    },
    [token],
  );

  useEffect(() => {
    fetchDoc();
  }, [fetchDoc]);

  const submitPasscode = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setPasscodeError(null);
    await fetchDoc(passcode.trim());
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Branded bar */}
      <header className="border-b border-border">
        <div className="mx-auto max-w-3xl px-4 py-4">
          <span className="font-serif text-lg font-semibold tracking-tight text-[var(--color-gold-dark)]">
            XYZ Strategy
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 sm:py-12">
        {status === 'loading' && (
          <div className="flex justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {status === 'gone' && (
          <div className="py-24 text-center">
            <h1 className="font-serif text-2xl font-semibold">Link unavailable</h1>
            <p className="mt-2 text-muted-foreground">
              This share link has expired or is no longer available.
            </p>
          </div>
        )}

        {status === 'error' && (
          <div className="py-24 text-center">
            <h1 className="font-serif text-2xl font-semibold">Something went wrong</h1>
            <p className="mt-2 text-muted-foreground">We couldn't load this document. Please try again.</p>
          </div>
        )}

        {status === 'passcode' && (
          <form onSubmit={submitPasscode} className="mx-auto max-w-sm py-16 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[rgba(184,150,62,0.15)]">
              <Lock className="h-6 w-6 text-[var(--color-gold-dark)]" />
            </div>
            <h1 className="font-serif text-xl font-semibold">Enter passcode</h1>
            <p className="mt-1 text-sm text-muted-foreground">This document is passcode protected.</p>
            <Input
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Passcode"
              className="mt-4 h-11 text-center"
              autoFocus
            />
            {passcodeError && <p className="mt-2 text-sm text-destructive">{passcodeError}</p>}
            <Button type="submit" className="mt-4 h-11 w-full" disabled={submitting || !passcode.trim()}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'View document'}
            </Button>
          </form>
        )}

        {status === 'ready' && doc && <DocumentBody doc={doc} />}
      </main>
    </div>
  );
}

function DocumentBody({ doc }: { doc: PublicDocument }) {
  const blocks = Array.isArray(doc.content) ? (doc.content as Block[]) : null;

  return (
    <article>
      <h1 className="font-serif text-3xl font-semibold tracking-tight sm:text-4xl">{doc.title}</h1>

      {blocks && blocks.length > 0 && (
        <div className="mt-8 space-y-6">
          {blocks.map((b, i) => (
            <section key={i}>
              {(b.heading || b.title) && (
                <h2 className="font-serif text-xl font-semibold">{b.heading || b.title}</h2>
              )}
              {(b.body || b.text) && (
                <p className="mt-2 leading-relaxed text-foreground/90 whitespace-pre-line">{b.body || b.text}</p>
              )}
              {b.items && b.items.length > 0 && (
                <ul className="mt-2 list-disc space-y-1 pl-5 text-foreground/90">
                  {b.items.map((it, j) => (
                    <li key={j}>{it}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      )}

      {!blocks && doc.raw_markdown && <Markdown source={doc.raw_markdown} />}

      {doc.external_url && (
        <div className="mt-8 rounded-md border border-border p-5 text-center">
          <p className="text-sm text-muted-foreground">The full document is hosted externally.</p>
          <Button asChild className="mt-3 h-11">
            <a href={doc.external_url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" />
              Open document
            </a>
          </Button>
        </div>
      )}

      <footer className="mt-16 border-t border-border pt-6 text-center text-xs text-muted-foreground">
        Shared by XYZ Strategy
      </footer>
    </article>
  );
}

/** Lightweight, safe markdown render (no raw HTML): headings, lists, paragraphs. */
function Markdown({ source }: { source: string }) {
  const lines = source.split('\n');
  const out: React.ReactNode[] = [];
  let list: string[] = [];

  const flushList = (key: string) => {
    if (list.length) {
      out.push(
        <ul key={key} className="my-3 list-disc space-y-1 pl-5 text-foreground/90">
          {list.map((it, i) => (
            <li key={i}>{it}</li>
          ))}
        </ul>,
      );
      list = [];
    }
  };

  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    if (line.startsWith('## ')) {
      flushList(`l${i}`);
      out.push(<h3 key={i} className="mt-6 font-serif text-lg font-semibold">{line.slice(3)}</h3>);
    } else if (line.startsWith('# ')) {
      flushList(`l${i}`);
      out.push(<h2 key={i} className="mt-6 font-serif text-xl font-semibold">{line.slice(2)}</h2>);
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      list.push(line.slice(2));
    } else if (line.trim() === '') {
      flushList(`l${i}`);
    } else {
      flushList(`l${i}`);
      out.push(<p key={i} className="my-3 leading-relaxed text-foreground/90">{line}</p>);
    }
  });
  flushList('last');

  return <div className="mt-8">{out}</div>;
}
